import { Fragment } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Menu, Transition } from '@headlessui/react'
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/20/solid'
import { BuildingOfficeIcon } from '@heroicons/react/24/outline'
import { useSalesforce } from '../hooks/useSalesforce'
import { useWorkspace } from '../context/WorkspaceContext'

interface OrgSwitcherProps {
  currentOrgId: string | null
}

export default function OrgSwitcher({ currentOrgId }: OrgSwitcherProps) {
  const { orgs, isLoading } = useSalesforce()
  const { setCurrentOrgId } = useWorkspace()
  const navigate = useNavigate()
  const location = useLocation()

  // Get current org details
  const currentOrg = orgs?.find(org => org.id === currentOrgId)

  // Sort orgs: by environment name first, then production first within each environment
  // This matches the OrgsPage grouping behavior
  const sortedOrgs = orgs ? [...orgs].sort((a, b) => {
    // First compare by environment (production org name)
    const envA = a.production_org_name || a.name
    const envB = b.production_org_name || b.name

    if (envA !== envB) {
      return envA.localeCompare(envB)
    }

    // Within same environment: production first, then sandboxes
    if (a.is_sandbox === b.is_sandbox) return 0
    return a.is_sandbox ? 1 : -1
  }) : []

  const handleOrgSwitch = (newOrgId: string) => {
    setCurrentOrgId(newOrgId)

    // Determine current page type
    const pathParts = location.pathname.split('/').filter(Boolean)
    let pageType = 'dashboard'

    if (pathParts.includes('flows')) pageType = 'flows'
    else if (pathParts.includes('entry-criteria') || pathParts.includes('triggers')) pageType = 'entry-criteria'
    else if (pathParts.includes('fields')) pageType = 'fields'
    else if (pathParts.includes('dashboard')) pageType = 'dashboard'

    // Navigate to the same page type with new org
    navigate(`/${newOrgId}/${pageType}`)
  }

  if (isLoading || !orgs || orgs.length === 0) {
    return null
  }

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className="inline-flex w-full justify-between items-center gap-x-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
          <div className="flex items-center gap-x-2">
            <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
            <span className="truncate max-w-[200px]">
              {currentOrg?.name || 'Select Org'}
            </span>
          </div>
          <ChevronDownIcon className="-mr-1 h-5 w-5 text-gray-400 flex-shrink-0" aria-hidden="true" />
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-10 mt-2 w-72 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1 max-h-96 overflow-y-auto">
            {sortedOrgs.map((org) => (
              <Menu.Item key={org.id}>
                {({ active }) => (
                  <button
                    onClick={() => handleOrgSwitch(org.id)}
                    className={`${
                      active ? 'bg-gray-100' : ''
                    } ${
                      org.id === currentOrgId ? 'bg-blue-50' : ''
                    } group flex w-full items-center justify-between px-4 py-2 text-sm text-gray-900`}
                  >
                    <span className="truncate font-medium">{org.name}</span>
                    {org.id === currentOrgId && (
                      <CheckIcon className="h-5 w-5 text-blue-600 flex-shrink-0 ml-2" />
                    )}
                  </button>
                )}
              </Menu.Item>
            ))}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  )
}
